import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import { AerosAutocomplete, type AerosAutocompleteProps } from "../AerosAutocomplete"

interface FormAutocompleteProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
  FreeSolo extends boolean | undefined = false,
> extends Omit<AerosAutocompleteProps<TValue, FreeSolo>, "name" | "defaultValue" | "value" | "onChange"> {
  control: Control<TFieldValues>
  name: TName
  onChange?: (value: TValue | null) => void
}

export const FormAutocomplete = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TValue extends string | number = string,
  FreeSolo extends boolean | undefined = false,
>({
  control,
  name,
  helperText,
  onChange,
  freeSolo = false as FreeSolo,
  ...props
}: FormAutocompleteProps<TFieldValues, TName, TValue, FreeSolo>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (value: TValue | null) => {
    field.onChange(value)
    onChange?.(value)
  }

  return (
    <AerosAutocomplete<TValue, FreeSolo>
      {...field}
      {...props}
      freeSolo={freeSolo}
      error={!!error}
      helperText={error?.message ?? helperText}
      onChange={handleChange}
    />
  )
}
